import rp from 'request-promise';
import Promise from 'bluebird';
import _ from 'lodash';

const API_KEY = 'keenly-wfl-test';

const REQUEST_OPTIONS = (auth) => ({
    json: true,
    headers: {
        'x-api-key': API_KEY,
        Authorization: auth ? `Bearer ${auth.currentUser.id_token}` : null,
    }
});

const callApi = (opts, auth) => rp(opts)
    .catch((err) => {
        if (err.error === 'SESSION EXPIRED') {
            console.log('Session is expired, refreshing');

            const {refresh_token} = auth.currentUser;
            let id_token;

            return rp({
                ...REQUEST_OPTIONS(),
                url: `${process.env.REACT_APP_API_ROOT}/login/refresh`,
                method: 'POST',
                body: {refresh_token},
            })
                .then((response) => {
                    id_token = response.id_token;
                    response.refresh_token = refresh_token;
                    return response;
                })
                .then(auth.onLogin)
                .then(() => rp({
                    ...opts,
                    ...REQUEST_OPTIONS({currentUser: {id_token}}),
                }));
        }
        // console.log('Error calling API', err);
        throw err;
    });

// rp.debug = true;

const Api = {
    login: (opts) => callApi({
        ...REQUEST_OPTIONS(),
        url: `${process.env.REACT_APP_API_ROOT}/login`,
        method: 'POST',
        body: opts,
    }),

    resetPassword: (opts) => callApi({
        ...REQUEST_OPTIONS(),
        url: `${process.env.REACT_APP_API_ROOT}/reset-password`,
        method: 'POST',
        body: opts,
    }),

    withAuth: (auth) => {
        const callWithAuth = (opts) => callApi(opts, auth);

        return {
            logout: () => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/logout`,
                method: 'POST',
            }),
            getFacility: (org, facilityId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/facility/${org}/${facilityId}`
            }),
            getFacilities: (org) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/${org}/facilities/conf_list`
            }),
            getOrganizationSummary: (org) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/${org}/summary`
            }),
            getOrganizationDetails: ({org, facility}) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/${org}/facilities/${facility}/conf_list`
            }),
            getSections: (facilityName) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facilityName}/sections`
            }),
            getRooms: ({ facility, section }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/rooms/index`
            }),
            getBeds: ({ facility, section }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/beds/index`
            }),
            getBedsNoIndex: ({ facility, section }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/beds`
            }),
            getRoom: ({ facility, section, room }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/rooms/${room}`
            }),

            getSetting: ({ id }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/settings/${id}`
            }),

            getDefaultSetting: ({ orgId}) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/settings/default/${orgId}`
            }),

            setSetting: ({ id, setting }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/settings/${id}`,
                method: 'POST',
                body: setting,
            }),

            getRoomData: ({ facility, section, room, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/rooms/${room}/data`,
                qs: { startTime, endTime }
            }),

            // KH-621 New Neighborhood API (Summary/Facility)
            getNeighborhoodSummaryAlerts: ({ facility, section, startTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/summary/alerts`,
                qs: { startTime }
            }),
            getNeighborhoodSummaryMovement: ({ facility, section }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/summary/movement`,
            }),
            getNeighborhoodSummaryOutOfRange: ({ facility, section, startTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/summary/out_of_range`,
                qs: { startTime }
            }),
            getNeighborhoodSummarySleep: ({ facility, section, startTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/summary/sleep`,
                qs: { startTime }
            }),

            getActiveAlerts: (patientId) => {
                return callWithAuth({
                    ...REQUEST_OPTIONS(auth),
                    url: `${process.env.REACT_APP_API_ROOT}/alerts/active/${patientId}`,
                });
            },

            getAlertsSummary: (patientId, timeStart = 0, timeEnd = 0) => {
                return callWithAuth({
                    ...REQUEST_OPTIONS(auth),
                    url: `${process.env.REACT_APP_API_ROOT}/alerts/patient/${patientId}?start=${timeStart}&end=${timeEnd}`,
                });
            },

            getPatientParamsOOR: ({ patient, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/params/outofrange/${patient}`,
                qs: { start: startTime, end: endTime }
            }),

            getPatientParamsMOV: ({ patient, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/params/movements/${patient}`,
                qs: { start: startTime, end: endTime }
            }),

            getPatientParamsLR: ({ patient, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/lastresp/${patient}`,
                qs: { startTime, endTime }
            }),

            getPatientParamsAvgResp: ({ patient, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/avgresp/${patient}`,
                qs: { start:startTime, end:endTime }
            }),

            getPatientParamsAlert: ({ patient, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/params/alerts/${patient}`,
                qs: { start: startTime, end: endTime }
            }),

            getPatientPoints: ({ patient, start, end }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/points/${patient}`,
                qs: { start, end }
            }),

            getPatientSettingResp: ({ patient }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/settings/resp/${patient}`,
            }),

            getSectionTransitions: ({ facility, section, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/transitions`,
                qs: { startTime, endTime }
            }),

            getSectionMovements: ({ facility, section, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/movements`,
                qs: { start: startTime, end: endTime }
            }),

            getSectionSleepData: ({ facility, section, startTime, endTime }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/sleep`,
                qs: { startTime, endTime }
            }),

            getPatients: ({ facility, section }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facility}/sections/${section}/patients`
            }),

            getRoomsWithPatients: (opts) => Promise.props({
                rooms: Api.withAuth(auth).getRooms(opts),
                patients: Api.withAuth(auth).getPatients(opts),
            }).then((data) => data.rooms.map((room) => (
                {...room, patient: _.find(data.patients, {room: room.room_id}
                )}))
            ),
            getBedsWithPatients: (opts) => Promise.props({
                beds: Api.withAuth(auth).getBeds(opts),
                patients: Api.withAuth(auth).getPatients(opts),
            }).then((data) => {
                    if (!data.beds || data.beds.length === 0) {
                        return [];
                    }
                    return data.beds.map((bed) => (
                        {...bed, patient: _.find(data.patients, {bed: bed.bed_id})}))
                }
            ),
            getPatientSleep: ({ id, startTimestamp, endTimestamp }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/sleep_days/${id}/${startTimestamp}/${endTimestamp}`,
            }),
            getPatientRespiration: ({ id, startTimestamp, endTimestamp, patientHighRR, patientLowRR }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/respiration_days/${id}/${startTimestamp}/${endTimestamp}/${patientHighRR}/${patientLowRR}`,
            }),
            getPatientOutOfRange: ({ id, startTimestamp, endTimestamp }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/outofrange_days/${id}/${startTimestamp}/${endTimestamp}`,
            }),
            getPatientMovementScore: ({ id, startTimestamp, endTimestamp }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/mvscore_days/${id}/${startTimestamp}/${endTimestamp}`,
            }),
            getAllPatients: ({ orgId }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/patient/${orgId}/patients/list`
            }),
            getPatient: ({ id }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/patient/${id}`
            }),
            createPatientConfTab: ({ orgId, patient }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/patient/${orgId}`,
                method: 'POST',
                body: patient,
            }),
            editPatientConfTab: ({ id, patient }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/patient/${id}`,
                method: 'PUT',
                body: patient,
            }),
            deletePatientConfTab: ({ orgId, id }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/patient/${orgId}/${id}`,
                method: 'DELETE',
            }),

            uploadPatientForm: ({ file, patientId }) => callWithAuth({
                url: `${process.env.REACT_APP_API_ROOT}/patient/consent`,
                method: 'POST',
                json: true,
                headers: {
                    'x-api-key': API_KEY,
                    Authorization: auth ? `Bearer ${auth.currentUser.id_token}` : null,
                    'Content-Type': 'multipart/form-data',
                },
                qs: { patientId },
                multipart: {
                    chunked: false,
                    data: [
                        {
                            'Content-Disposition': `form-data; name="filedata"; filename="${file.filename}"`,
                            'Content-Type': file.mimetype,
                            body: file.data
                        }
                    ]
                }
            }),

            deletePatientForm: ({ file, patientId }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/patient/consent/${file}`,
                method: 'DELETE',
                qs: { patientId },
            }),
            getDevices: (org) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/${org}/devices/conf_list`
            }),
            unassignDevices: (device_id) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'POST',
                url: `${process.env.REACT_APP_API_ROOT}/devices/unassign`,
                body: device_id
            }),
            getOrgDeviceEditList: (org) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/org/${org}/devices/edit_list`
            }),
            editDevices: (devices) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'POST',
                url: `${process.env.REACT_APP_API_ROOT}/devices/edit`,
                body: devices
            }),

            postClearAlert: (alertId, alertBody) =>
                callWithAuth({
                    ...REQUEST_OPTIONS(auth),
                    method: 'POST',
                    url: `${process.env.REACT_APP_API_ROOT}/alerts/deactivate/${alertId}`,
                    body: alertBody,
                }),

            createFacility: ({ org, timezone, isHomeCare }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'POST',
                url: `${process.env.REACT_APP_API_ROOT}/org/${auth.currentUser.defaultOrg}/facilities/${org}`,
                body: {
                    timezone,
                    isHomeCare
                }
            }),

            updateFacility: ({ orgId, facilityId, timezone, isHomeCare }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'PUT',
                url: `${process.env.REACT_APP_API_ROOT}/org/facility/${orgId}/${facilityId}`,
                body: {
                    timezone,
                    isHomeCare
                }
            }),

            deleteFacility: ({ orgId, facilityId }) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'DELETE',
                url: `${process.env.REACT_APP_API_ROOT}/org/facility/${orgId}/${facilityId}`,
            }),
            getSectionsNames: (facilityId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'GET',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facilityId}/sections`,
            }),

            getBedsByFacility: (facilityId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'GET',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facilityId}/sections/conf_list`,
            }),

            createBed: (facilityId, beds) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'POST',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facilityId}/sections/bed`,
                body: beds
            }),

            updateBed: (facilityId, sectionName, bedId, bed) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'PUT',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facilityId}/sections/${sectionName}/beds/${bedId}`,
                body: bed
            }),

            deleteBed: (bedId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'DELETE',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${bedId}`,
            }),

            deleteBeds: (beds) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'POST',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/delbeds`,
                body: beds
            }),

            importBed: (facilityId, data) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'POST',
                url: `${process.env.REACT_APP_API_ROOT}/facilities/${facilityId}/sections/bedcsv`,
                body: data
            }),
            getUsers: ({orgId}) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                method: 'GET',
                url: `${process.env.REACT_APP_API_ROOT}/org/${orgId}/users`
            }),
            getLatestDailyReport: (patientId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/api/reports/daily/latest/${patientId}`,
            }),

            getWeekDailyReports: (patientId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/api/reports/daily/week/${patientId}`,
            }),

            getLatestWellnessReport: (patientId) => callWithAuth({
                ...REQUEST_OPTIONS(auth),
                url: `${process.env.REACT_APP_API_ROOT}/api/reports/wellness/latest/${patientId}`,
            }),
        };
    }
};

export default Api;